import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, GearSix, ArrowLeft, CurrencyDollar, Percent, Users, HardDrives } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminFeesPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [percentageFee, setPercentageFee] = useState('');
  const [flatFee, setFlatFee] = useState('');
  const [feePayer, setFeePayer] = useState('sender');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [storageMethod, setStorageMethod] = useState('local');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [feeRes, storageRes] = await Promise.all([
        adminAPI.getFees(),
        adminAPI.getStorage()
      ]);
      setSettings(feeRes.data);
      setPercentageFee(String(feeRes.data.percentage_fee));
      setFlatFee(String(feeRes.data.flat_fee));
      setFeePayer(feeRes.data.fee_payer || 'sender');
      setTotalRevenue(feeRes.data.total_revenue || 0);
      setStorageMethod(storageRes.data?.method || 'local');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const pctVal = parseFloat(percentageFee);
    const flatVal = parseFloat(flatFee);

    if (isNaN(pctVal) || pctVal < 0 || pctVal > 50) {
      toast.error('Percentage fee must be between 0 and 50');
      return;
    }
    if (isNaN(flatVal) || flatVal < 0 || flatVal > 100) {
      toast.error('Flat fee must be between $0 and $100');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.updateFees({
        percentage_fee: pctVal,
        flat_fee: flatVal,
        fee_payer: feePayer,
      });
      toast.success('Fee settings updated!');
      await loadSettings();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  // Example calculation
  const exampleAmount = 100;
  const exampleFee = (() => {
    const pct = parseFloat(percentageFee) || 0;
    const flat = parseFloat(flatFee) || 0;
    return round2((exampleAmount * pct / 100) + flat);
  })();

  function round2(n) { return Math.round(n * 100) / 100; }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-white text-lg font-bold mb-2">Access Denied</p>
            <p className="text-[#999999] mb-6">Only administrators can access fee settings.</p>
            <Link to="/dashboard">
              <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#22C55E] rounded-sm flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" weight="bold" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>Divispend</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <Link to="/admin/fees"><Button variant="ghost" className="text-[#22C55E] bg-[#22C55E]/10 rounded-sm text-sm h-8 px-3">Fees</Button></Link>
          <Link to="/admin/analytics"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm h-8 px-3">Analytics</Button></Link>
          <Link to="/admin/users"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm h-8 px-3">Users</Button></Link>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#22C55E]/10 rounded-sm flex items-center justify-center">
            <GearSix className="w-6 h-6 text-[#22C55E]" weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Platform Fee Settings
            </h1>
            <p className="text-sm text-[#999999]">Configure administration fees for all transactions</p>
          </div>
        </div>

        {/* Revenue Card */}
        <div className="flex gap-4 mb-6">
          <Card className="flex-1 bg-gradient-to-br from-[#22C55E] to-[#16A34A] text-white border-none rounded-sm" data-testid="revenue-card">
            <CardContent className="p-6">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/70 mb-1">Total Fee Revenue</p>
              <p className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="total-revenue">
                {formatCurrency(totalRevenue)}
              </p>
            </CardContent>
          </Card>
          <Link to="/admin/analytics" className="block">
            <Card className="h-full bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none hover:border-[#22C55E] transition-colors cursor-pointer" data-testid="analytics-link-card">
              <CardContent className="p-6 flex flex-col items-center justify-center h-full">
                <GearSix className="w-6 h-6 text-[#22C55E] mb-2" weight="duotone" />
                <p className="text-sm font-semibold text-[#22C55E]">View Analytics</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Fee Configuration */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="fee-settings-card">
          <CardHeader className="p-6 pb-4">
            <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Fee Structure
            </CardTitle>
            <CardDescription className="text-[#999999]">
              Fees applied on all transfers and product purchases
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] flex items-center gap-2">
                  <Percent className="w-3 h-3" /> Percentage Fee
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="50"
                    value={percentageFee}
                    onChange={(e) => setPercentageFee(e.target.value)}
                    className="rounded-sm bg-[#111111] border-[#333333] text-white focus:ring-2 focus:ring-[#22C55E] focus:ring-offset-1 focus:ring-offset-[#1A1A1A] pr-8"
                    data-testid="percentage-fee-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999999] text-sm">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] flex items-center gap-2">
                  <CurrencyDollar className="w-3 h-3" /> Flat Fee
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999] text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={flatFee}
                    onChange={(e) => setFlatFee(e.target.value)}
                    className="rounded-sm bg-[#111111] border-[#333333] text-white pl-7 focus:ring-2 focus:ring-[#22C55E] focus:ring-offset-1 focus:ring-offset-[#1A1A1A]"
                    data-testid="flat-fee-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] flex items-center gap-2">
                <Users className="w-3 h-3" /> Who Pays the Fee?
              </Label>
              <Select value={feePayer} onValueChange={setFeePayer}>
                <SelectTrigger className="rounded-sm bg-[#111111] border-[#333333] text-white focus:ring-2 focus:ring-[#22C55E]" data-testid="fee-payer-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                  <SelectItem value="sender" className="text-white hover:bg-[#333333]">Sender pays the fee</SelectItem>
                  <SelectItem value="receiver" className="text-white hover:bg-[#333333]">Receiver pays the fee</SelectItem>
                  <SelectItem value="split" className="text-white hover:bg-[#333333]">Split equally</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Example Preview */}
            <div className="bg-[#111111] rounded-sm p-4 border border-[#333333]" data-testid="fee-preview">
              <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-3">Preview: $100 transfer</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#999999]">Transfer amount</span>
                  <span className="text-white">$100.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#999999]">Fee ({percentageFee}% + ${parseFloat(flatFee || 0).toFixed(2)})</span>
                  <span className="text-[#22C55E]">${exampleFee.toFixed(2)}</span>
                </div>
                <div className="border-t border-[#333333] pt-1 flex justify-between font-bold">
                  <span className="text-[#999999]">
                    {feePayer === 'sender' ? 'Sender pays' : feePayer === 'receiver' ? 'Deducted from recipient' : 'Split fee'}
                  </span>
                  <span className="text-white">${exampleFee.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold"
              data-testid="save-fees-button"
            >
              {saving ? 'Saving...' : 'Save Fee Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Storage Settings */}
        <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none mb-6" data-testid="storage-settings-card">
          <CardHeader className="p-6 pb-4">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <HardDrives className="w-5 h-5 text-[#22C55E]" weight="duotone" />
              Storage Settings
            </CardTitle>
            <CardDescription className="text-[#999999]">
              Configure how product images are stored
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-4">
            <Select value={storageMethod} onValueChange={async (val) => {
              try {
                await adminAPI.updateStorage({ method: val });
                setStorageMethod(val);
                toast.success(`Storage method updated to ${val}`);
              } catch (error) {
                toast.error(formatApiError(error));
              }
            }}>
              <SelectTrigger className="rounded-sm bg-[#111111] border-[#333333] text-white" data-testid="storage-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                <SelectItem value="local" className="text-white hover:bg-[#333333]">Local Server Storage</SelectItem>
                <SelectItem value="cloud" className="text-white hover:bg-[#333333]">Cloud Storage (Coming Soon)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#666666]">
              {storageMethod === 'local' 
                ? 'Images stored on the application server. Simple and immediate.' 
                : 'Cloud storage for better scalability. Requires additional configuration.'}
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
